import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { BatchCosignItem } from "@/features/cosignature/types";
import { CosignNoteModal } from "@/features/cosignature/components/CosignNoteModal";
import { RejectNoteModal } from "@/features/cosignature/components/RejectNoteModal";

import { BatchCosignNotesModal } from "./(batch)/components/BatchCosignNotesModal";
import { BatchRejectNotesModal } from "./(batch)/components/BatchRejectNotesModal";
import { getNoteId, type AdaptedQueueRow } from "./(table)/helpers/adapters";
import type { NoteAction } from "./types";

type ModalState = { action: NoteAction; row: AdaptedQueueRow };
type BatchModalState = { action: "cosign" | "reject"; items: BatchCosignItem[] };

type CosignatureModalContextValue = {
  openBatchModal: (state: BatchModalState) => void;
  openModal: ({ action, row }: ModalState) => void;
};

const CosignatureModalContext = createContext<Nullable<CosignatureModalContextValue>>(null);

export function CosignatureModalProvider({
  children,
  onBatchSuccess,
}: {
  children: ReactNode;
  onBatchSuccess?: VoidFunction;
}) {
  const [modalState, setModalState] = useState<Nullable<ModalState>>(null);
  const [batchModalState, setBatchModalState] = useState<Nullable<BatchModalState>>(null);

  const openModal = useCallback(({ action, row }: ModalState) => {
    setModalState({ action, row });
  }, []);

  const openBatchModal = useCallback((state: BatchModalState) => {
    setBatchModalState(state);
  }, []);

  const handleClose = useCallback(() => setModalState(null), []);
  const handleBatchClose = useCallback(() => setBatchModalState(null), []);

  // stable reference prevents consumers from re-rendering on modal open/close
  const value = useMemo(() => ({ openBatchModal, openModal }), [openModal, openBatchModal]);

  return (
    <CosignatureModalContext.Provider value={value}>
      {children}

      {modalState?.action === "cosign" && (
        <CosignNoteModal
          documentId={modalState.row.id}
          documentType={modalState.row.documentType}
          isOpen
          noteId={getNoteId(modalState.row)}
          onClose={handleClose}
          patientId={modalState.row.patientId}
          providerName={modalState.row.submittedBy}
        />
      )}

      {modalState?.action === "reject" && (
        <RejectNoteModal
          documentId={modalState.row.id}
          documentType={modalState.row.documentType}
          isOpen
          noteId={getNoteId(modalState.row)}
          onClose={handleClose}
          patientId={modalState.row.patientId}
          providerName={modalState.row.submittedBy}
        />
      )}

      {batchModalState?.action === "cosign" && (
        <BatchCosignNotesModal
          isOpen
          items={batchModalState.items}
          onClose={handleBatchClose}
          onSuccess={onBatchSuccess}
        />
      )}

      {batchModalState?.action === "reject" && (
        <BatchRejectNotesModal
          isOpen
          items={batchModalState.items}
          onClose={handleBatchClose}
          onSuccess={onBatchSuccess}
        />
      )}
    </CosignatureModalContext.Provider>
  );
}

export function useCosignatureModal() {
  const ctx = useContext(CosignatureModalContext);

  if (!ctx) throw new Error("useCosignatureModal must be used within CosignatureModalProvider");

  return ctx;
}
